import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Coins, Loader2, Package, CheckCircle, Sparkles } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useCredits } from "@/hooks/useCredits";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface CreditPack {
  id: string;
  name: string;
  credits: number;
  price_cents: number;
  description: string | null;
  sort_order: number;
}

const BuyCredits = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { balance, refetch: refetchCredits } = useCredits();
  
  const [creditPacks, setCreditPacks] = useState<CreditPack[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);

  useEffect(() => {
    loadCreditPacks();
  }, []);

  // On return from PayPal approval, capture the order and credit the account.
  useEffect(() => {
    const orderId = searchParams.get('token'); // PayPal appends ?token=<orderId>
    const success = searchParams.get('success');
    if (orderId && success === 'true' && user) {
      verifyPurchase(orderId);
    }
  }, [searchParams, user]);

  const loadCreditPacks = async () => {
    try {
      const { data, error } = await supabase
        .from('credit_packs')
        .select('*')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;

      setCreditPacks(data || []);
    } catch (error) {
      console.error('Error loading credit packs:', error);
      toast.error('Failed to load credit packs');
    } finally {
      setIsLoading(false);
    }
  };

  const verifyPurchase = async (orderId: string) => {
    setIsVerifying(true);
    try {
      const { data, error } = await supabase.functions.invoke('verify-credit-pack-purchase', {
        body: { order_id: orderId }
      });

      if (error) throw error;

      if (data.success) {
        toast.success(`🎉 ${data.credits_added} credits added to your account!`);
        refetchCredits();
        // Clear the URL params
        navigate('/buy-credits', { replace: true });
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      // Don't show error - the credits may have already been added
    } finally {
      setIsVerifying(false);
    }
  };

  const handlePurchase = async (pack: CreditPack) => {
    if (!user) { navigate('/auth'); return; }
    setPurchaseLoading(pack.id);
    try {
      const { data, error } = await supabase.functions.invoke('purchase-credit-pack', {
        body: { pack_id: pack.id }
      });
      if (error || !data?.success || !data?.checkout_url) {
        throw new Error(data?.error || error?.message || 'Failed to start checkout');
      }
      window.location.href = data.checkout_url;
    } catch (error: any) {
      toast.error(error.message || 'Failed to start checkout');
      setPurchaseLoading(null);
    }
  };

  const formatPrice = (cents: number) => {
    return `$${(cents / 100).toFixed(2)}`;
  };

  const getPricePerCredit = (pack: CreditPack) => {
    return (pack.price_cents / pack.credits / 100).toFixed(2);
  };

  const getBestValue = () => {
    if (creditPacks.length === 0) return null;
    let best = creditPacks[0];
    let bestRatio = best.price_cents / best.credits;
    
    for (const pack of creditPacks) {
      const ratio = pack.price_cents / pack.credits;
      if (ratio < bestRatio) {
        bestRatio = ratio;
        best = pack;
      }
    }
    return best.id;
  };

  const bestValueId = getBestValue();

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background to-secondary/10 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-muted-foreground mb-4">Please sign in to purchase credits.</p>
            <Button onClick={() => navigate('/auth')}>Sign In</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-secondary/10">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <Link to="/" className="text-2xl font-bold text-primary">TailorWiz</Link>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="gap-1 text-base px-3 py-1">
                <Coins className="w-4 h-4" />
                {balance} credits
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
            <Package className="w-5 h-5" />
            <span className="font-medium">Credit Packs</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
            Need More Credits?
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Top up anytime — credits land on your account the moment your payment clears. No subscription required, credits never expire!
          </p>
        </div>
      </section>

      {/* Credit Packs Grid */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto">
          {isLoading || isVerifying ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              {isVerifying && <p className="text-muted-foreground">Verifying your purchase...</p>}
            </div>
          ) : creditPacks.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No credit packs available at the moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {creditPacks.map((pack) => {
                const isBestValue = pack.id === bestValueId;
                const isLoading = purchaseLoading === pack.id;

                return (
                  <Card 
                    key={pack.id} 
                    className={`relative transition-all hover:shadow-lg ${
                      isBestValue ? 'ring-2 ring-primary shadow-lg' : ''
                    }`}
                  >
                    {isBestValue && (
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-primary text-primary-foreground px-3 py-1 flex items-center gap-1">
                          <Sparkles className="w-3 h-3" />
                          Best Value
                        </Badge>
                      </div>
                    )}
                    
                    <CardHeader className="text-center pt-8">
                      <div className="mx-auto w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                        <Coins className="w-7 h-7 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{pack.name}</CardTitle>
                      <CardDescription>{pack.description}</CardDescription>
                    </CardHeader>
                    
                    <CardContent className="text-center space-y-4">
                      <div>
                        <div className="text-4xl font-bold text-foreground">
                          {pack.credits}
                        </div>
                        <div className="text-sm text-muted-foreground">credits</div>
                      </div>
                      
                      <div className="border-t pt-4">
                        <div className="text-3xl font-bold text-foreground">
                          {formatPrice(pack.price_cents)}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          ${getPricePerCredit(pack)} per credit
                        </div>
                      </div>

                      <Button 
                        className="w-full" 
                        size="lg"
                        onClick={() => handlePurchase(pack)}
                        disabled={isLoading}
                      >
                        {isLoading ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>Buy Now</>
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-3xl">
          <Card>
            <CardContent className="pt-6">
              <div className="grid sm:grid-cols-3 gap-6 text-center">
                <div className="space-y-2">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto" />
                  <h3 className="font-semibold">No Expiration</h3>
                  <p className="text-sm text-muted-foreground">Credits never expire. Use them whenever you need.</p>
                </div>
                <div className="space-y-2">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto" />
                  <h3 className="font-semibold">Instant Delivery</h3>
                  <p className="text-sm text-muted-foreground">Credits added to your account immediately.</p>
                </div>
                <div className="space-y-2">
                  <CheckCircle className="w-8 h-8 text-green-500 mx-auto" />
                  <h3 className="font-semibold">Secure Payment</h3>
                  <p className="text-sm text-muted-foreground">Powered by Square for secure transactions.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Link to Subscription */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 text-center">
        <p className="text-muted-foreground mb-4">
          Want monthly credits? Check out our subscription plans.
        </p>
        <Button variant="outline" onClick={() => navigate('/pricing')}>
          View Subscription Plans
        </Button>
      </section>
    </div>
  );
};

export default BuyCredits;
